import { useEffect, useRef, useSyncExternalStore } from "react";

/** Whether this is a Mac, where a command is ⌘ and not Ctrl. */
export function onMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
}

/** The key held for a command, as it is printed on the keyboard in front of somebody. */
export function commandKey(): string {
  return onMac() ? "⌘" : "Ctrl";
}

/**
 * Whether a key event happened somewhere text is being typed, where a
 * shortcut like "/" is the character wanted rather than a command.
 */
export function typingIn(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable
  );
}

/**
 * Whether something is open over the page: a dialog, a side panel, the
 * search box, a date picker or a menu. A single key means something else in
 * there, or nothing, and must not move the page underneath.
 */
export function layerOpen(): boolean {
  return document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"]') !== null;
}

/**
 * Whether a command key is held. Ctrl and Alt together are left alone: that
 * is how AltGr arrives, and on many keyboards AltGr is how "[" is typed.
 */
function commanding(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey !== event.altKey;
}

const MODIFIERS = new Set(["Shift", "Control", "Alt", "Meta", "AltGraph", "CapsLock"]);

/** How long G waits for the letter that follows it. */
const CHORD_MS = 1500;

export interface Shortcuts {
  /** Single keys, by the character they type: "?" and "[". */
  keys: Record<string, () => void>;
  /** The letter that follows G, and what it does. */
  go: Record<string, () => void>;
}

/**
 * The single-key shortcuts of the shell: one key, or G and then a letter.
 *
 * None fires while somebody is typing in a field, while a command key is
 * held, or while anything is open over the page. It listens ahead of the
 * page, so that the letter after G is read as a place to go and never
 * reaches a row that would take the same letter as "approve" or "decline";
 * a letter after G that goes nowhere is swallowed for the same reason.
 */
export function useShortcuts(shortcuts: Shortcuts) {
  const latest = useRef(shortcuts);
  useEffect(() => {
    latest.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    // The timer running while G waits for its letter; 0 when it is not.
    let waiting = 0;
    function forget() {
      window.clearTimeout(waiting);
      waiting = 0;
    }

    function onKey(event: KeyboardEvent) {
      if (event.repeat || event.isComposing || MODIFIERS.has(event.key)) return;
      if (commanding(event) || typingIn(event.target) || layerOpen()) {
        forget();
        return;
      }
      const { keys, go } = latest.current;
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;

      if (waiting) {
        forget();
        if (Object.hasOwn(go, key)) {
          event.preventDefault();
          event.stopPropagation();
          go[key]?.();
          return;
        }
        if (/^[a-z]$/.test(key)) {
          event.stopPropagation();
          return;
        }
      }

      if (key === "g" && Object.keys(go).length > 0) {
        waiting = window.setTimeout(forget, CHORD_MS);
        return;
      }
      if (Object.hasOwn(keys, key)) {
        event.preventDefault();
        keys[key]?.();
      }
    }

    window.addEventListener("keydown", onKey, true);
    return () => {
      forget();
      window.removeEventListener("keydown", onKey, true);
    };
  }, []);
}

/**
 * Whether the sheet of shortcuts is open.
 *
 * It is opened from three places that do not know about each other — the
 * "?" key, the account menu and the search box — and drawn once, by the
 * shell. So it is a note left here, the way `lib/create.ts` leaves one.
 */
let sheetOpen = false;
const watching = new Set<() => void>();

export function showShortcuts(open = true) {
  if (sheetOpen === open) return;
  sheetOpen = open;
  for (const notify of [...watching]) notify();
}

function watch(notify: () => void) {
  watching.add(notify);
  return () => watching.delete(notify);
}

/** Whether the sheet is open, and the way to open or close it. */
export function useShortcutSheet(): [boolean, (open: boolean) => void] {
  const open = useSyncExternalStore(
    watch,
    () => sheetOpen,
    () => false,
  );
  return [open, showShortcuts];
}
